import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { BazaarListingCard, CATEGORY_LABELS, formatPrice } from "../components/bazaar/BazaarListingCard";
import { BazaarListingModal } from "../components/bazaar/BazaarListingModal";
import { MerchantOnboardingModal } from "../components/bazaar/MerchantOnboardingModal";
import { BuyerTermsModal, hasSeenBuyerTerms } from "../components/bazaar/BuyerTermsModal";
import { ArrowLeft, ShoppingCart, Store, Trash2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CART_STORAGE_KEY = "bazaar_cart_v1";
const BAZAAR_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/34fo060o_bazaar.png";

export default function BazaarPage() {
  const { checkAuth, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_STORAGE_KEY)) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [listingModalOpen, setListingModalOpen] = useState(false);
  const [merchantModalOpen, setMerchantModalOpen] = useState(false);
  const [buyerTermsOpen, setBuyerTermsOpen] = useState(false);
  const [merchantStatus, setMerchantStatus] = useState({ has_account: false, onboarding_complete: false, merchant_profile: null });
  const [onboarding, setOnboarding] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);

  useEffect(() => { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart)); }, [cart]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bazaar/listings", { params: { category: category !== "all" ? category : undefined, search: search || undefined } });
      setListings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadListings(); }, [category, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/bazaar/merchant/status").then(({ data }) => setMerchantStatus(data)).catch(() => {});
    if (new URLSearchParams(window.location.search).get("done")) {
      checkAuth();
      toast.success("Onboarding info received — Stripe is verifying your details.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "purchases") api.get("/bazaar/orders/mine").then(({ data }) => setPurchases(data)).catch(() => {});
    if (tab === "sales") api.get("/bazaar/orders/sales").then(({ data }) => setSales(data)).catch(() => {});
  }, [tab]);

  const toggleCart = (listing) => {
    setCart((prev) => (prev.some((l) => l.id === listing.id) ? prev.filter((l) => l.id !== listing.id) : [...prev, listing]));
  };

  const cartTotal = useMemo(() => cart.reduce((sum, l) => sum + l.price_cents, 0), [cart]);

  const onboardMerchant = async () => {
    setOnboarding(true);
    try {
      const { data } = await api.post("/bazaar/merchant/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start onboarding");
      setOnboarding(false);
    }
  };

  const startBecomeMerchant = () => {
    if (merchantStatus.merchant_profile) return onboardMerchant();
    setMerchantModalOpen(true);
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/bazaar/checkout", { listing_ids: cart.map((l) => l.id), origin_url: window.location.origin });
      setCart([]);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout");
      setCheckingOut(false);
    }
  };

  const startCheckout = () => {
    if (hasSeenBuyerTerms(user?.id)) return checkout();
    setBuyerTermsOpen(true);
  };

  return (
    <div className="relative min-h-screen text-[#f1e0c5] bg-[#151613] bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${BAZAAR_BG_URL}')` }} data-testid="bazaar-page">
      <div className="fixed inset-0 bg-black/70 pointer-events-none" data-testid="bazaar-bg-overlay" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button data-testid="bazaar-back-button" variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-2"><Store className="w-7 h-7 text-[#D4AF37]" /> The Bazaar</h1>
          </div>
          <div className="flex items-center gap-2">
            {merchantStatus.onboarding_complete ? (
              <Button data-testid="bazaar-sell-item-button" onClick={() => setListingModalOpen(true)} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e]">Sell an Item</Button>
            ) : (
              <Button data-testid="bazaar-become-merchant-button" onClick={startBecomeMerchant} disabled={onboarding} variant="secondary" className="rounded-full gap-2">
                {onboarding && <Loader2 className="w-4 h-4 animate-spin" />} {merchantStatus.has_account ? "Finish Merchant Setup" : "Become a Merchant"}
              </Button>
            )}
            <Button data-testid="bazaar-cart-open-button" variant="secondary" onClick={() => setCartOpen(true)} className="rounded-full gap-2 relative">
              <ShoppingCart className="w-4 h-4" /> Cart
              {cart.length > 0 && <span data-testid="bazaar-cart-count" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#D90429] text-white text-[10px] flex items-center justify-center">{cart.length}</span>}
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-black/30" data-testid="bazaar-tabs">
            <TabsTrigger data-testid="bazaar-tab-browse" value="browse">Browse</TabsTrigger>
            <TabsTrigger data-testid="bazaar-tab-purchases" value="purchases">My Purchases</TabsTrigger>
            {merchantStatus.onboarding_complete && <TabsTrigger data-testid="bazaar-tab-sales" value="sales">My Sales</TabsTrigger>}
          </TabsList>
        </Tabs>

        {tab === "browse" && (
          <>
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={() => setCategory("all")} data-testid="bazaar-category-all" className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wide border transition-colors ${category === "all" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#f1e0c5]/60 hover:border-[#D4AF37]/50"}`}>All</button>
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                <button key={v} onClick={() => setCategory(v)} data-testid={`bazaar-category-${v}`} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wide border transition-colors ${category === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#f1e0c5]/60 hover:border-[#D4AF37]/50"}`}>{l}</button>
              ))}
              <Input data-testid="bazaar-search-input" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-black/30 border-white/10 w-48 h-8 text-xs ml-auto" />
            </div>

            {loading ? (
              <p className="text-sm text-[#f1e0c5]/40 py-10 text-center">Loading the stalls…</p>
            ) : listings.length === 0 ? (
              <div className="text-center py-16 text-[#f1e0c5]/40 text-sm" data-testid="bazaar-empty">Nothing here yet — be the first Merchant to list something!</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="bazaar-listing-grid">
                {listings.map((l) => (
                  <BazaarListingCard key={l.id} listing={l} inCart={cart.some((c) => c.id === l.id)} onToggleCart={toggleCart} />
                ))}
              </div>
            )}
          </>
        )}

        {tab === "purchases" && (
          <div className="space-y-3" data-testid="bazaar-purchases-list">
            {purchases.length === 0 ? (
              <div className="text-center py-16 text-[#f1e0c5]/40 text-sm">No purchases yet — browse the Bazaar to find something good.</div>
            ) : purchases.flatMap((o) => o.items).map((it, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#1c1712] p-3" data-testid={`bazaar-purchase-item-${it.listing_id}`}>
                <span className="text-sm">{it.title}</span>
                <a href={it.file_url} target="_blank" rel="noreferrer" data-testid={`bazaar-purchase-download-${it.listing_id}`}>
                  <Button size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs"><Download className="w-3.5 h-3.5" /> Download</Button>
                </a>
              </div>
            ))}
          </div>
        )}

        {tab === "sales" && (
          <div className="space-y-3" data-testid="bazaar-sales-list">
            {sales.length === 0 ? (
              <div className="text-center py-16 text-[#f1e0c5]/40 text-sm">No sales yet — list something to start earning.</div>
            ) : sales.map((s, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#1c1712] p-3" data-testid={`bazaar-sale-item-${i}`}>
                <div>
                  <p className="text-sm">{s.title}</p>
                  <p className="text-[10px] text-[#f1e0c5]/40 uppercase tracking-wide">{s.payout_status === "transferred" ? "Paid out" : s.payout_status === "transfer_failed" ? "Payout failed — check your Stripe account" : "Pending payout"}</p>
                </div>
                <span className="text-[#D4AF37] font-display">{formatPrice(s.seller_cut_cents ?? Math.round(s.price_cents * 0.85))}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right" className="bg-[#1c1712] border-white/10 text-[#f1e0c5] w-full sm:max-w-md flex flex-col" data-testid="bazaar-cart-drawer">
          <SheetHeader><SheetTitle className="font-display text-2xl">Your Cart</SheetTitle></SheetHeader>
          {cart.length === 0 ? (
            <p className="text-sm text-[#f1e0c5]/40 py-10 text-center">Your cart is empty.</p>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-2">
              {cart.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 p-2" data-testid={`bazaar-cart-item-${l.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{l.title}</p>
                    <p className="text-xs text-[#D4AF37]">{formatPrice(l.price_cents)}</p>
                  </div>
                  <button onClick={() => toggleCart(l)} data-testid={`bazaar-cart-remove-${l.id}`} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          {cart.length > 0 && (
            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="flex justify-between text-sm"><span>Total</span><span className="font-display text-xl text-[#D4AF37]" data-testid="bazaar-cart-total">{formatPrice(cartTotal)}</span></div>
              <Button data-testid="bazaar-checkout-button" onClick={startCheckout} disabled={checkingOut} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
                {checkingOut ? "Redirecting…" : "Checkout"}
              </Button>
              <Link to="/legal/buyer-terms" target="_blank" data-testid="bazaar-buyer-terms-link" className="block text-center text-[10px] text-[#f1e0c5]/30 hover:text-[#D4AF37] underline">Buyer Terms</Link>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <BazaarListingModal open={listingModalOpen} onOpenChange={setListingModalOpen} onCreated={() => { setTab("browse"); loadListings(); }} />
      <MerchantOnboardingModal open={merchantModalOpen} onOpenChange={setMerchantModalOpen} userId={user?.id} defaultEmail={user?.email} onContinueToStripe={onboardMerchant} />
      <BuyerTermsModal open={buyerTermsOpen} onOpenChange={setBuyerTermsOpen} userId={user?.id} onContinue={checkout} />
    </div>
  );
}
